import type {
  ApprovalStatus,
  ConversationAgentState,
  ConnectionStatus,
  DeskStatus,
  ExecutionStatus,
  WorkItemStatus,
} from '@deskworks/shared-types';
import { Badge, dotTone } from '@/components/ui/badge';
import { t } from '@/lib/i18n';
import {
  AGENT_STATUS,
  APPROVAL_STATUS,
  CONNECTION_STATUS,
  CONVERSATION_STATE,
  DESK_STATUS,
  EXECUTION_STATUS,
  WORK_ITEM_STATUS,
  type StatusMeta,
} from '@/lib/labels';
import { cn } from '@/lib/utils';

const FALLBACK: StatusMeta = { label: t('Unknown'), tone: 'neutral' };

export function MetaBadge({ meta, className, showHint = true }: { meta: StatusMeta | undefined; className?: string; showHint?: boolean }) {
  const m = meta ?? FALLBACK;
  return (
    <Badge tone={m.tone} className={className} title={showHint ? m.hint : undefined}>
      <span className={cn('size-1.5 rounded-full', dotTone[m.tone], m.live && 'animate-soft-pulse')} />
      {m.label}
    </Badge>
  );
}

export function WorkItemStatusBadge({ status, paused, className }: { status: WorkItemStatus; paused?: boolean; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <MetaBadge meta={WORK_ITEM_STATUS[status] ?? { label: status, tone: 'neutral' }} />
      {paused && <Badge tone="amber">{t('Paused')}</Badge>}
    </span>
  );
}

export function ExecutionStatusBadge({ status }: { status: ExecutionStatus }) {
  return <MetaBadge meta={EXECUTION_STATUS[status] ?? { label: status, tone: 'neutral' }} />;
}

export function DeskStatusBadge({ status }: { status: DeskStatus }) {
  return <MetaBadge meta={DESK_STATUS[status] ?? { label: status, tone: 'neutral' }} />;
}

export function ConnectionStatusBadge({ status }: { status: ConnectionStatus }) {
  return <MetaBadge meta={CONNECTION_STATUS[status] ?? { label: status, tone: 'neutral' }} />;
}

export function ApprovalStatusBadge({ status }: { status: ApprovalStatus }) {
  return <MetaBadge meta={APPROVAL_STATUS[status] ?? { label: status, tone: 'neutral' }} />;
}

export function AgentStatusBadge({ status }: { status: 'active' | 'disabled' }) {
  return <MetaBadge meta={AGENT_STATUS[status] ?? { label: status, tone: 'neutral' }} />;
}

export function StatusDot({ status, className }: { status: WorkItemStatus; className?: string }) {
  const m = WORK_ITEM_STATUS[status] ?? FALLBACK;
  return (
    <span
      title={m.label}
      className={cn('inline-block size-2 shrink-0 rounded-full', dotTone[m.tone], m.live && 'animate-soft-pulse', className)}
    />
  );
}

export function ConversationStateBadge({ state }: { state: ConversationAgentState }) {
  return <MetaBadge meta={CONVERSATION_STATE[state] ?? { label: state, tone: 'neutral' }} />;
}
